import type { ConversationLanguageCode } from 'lib-shared-languages';
import type { Conversation } from 'lib-shared-legacy-types';
import { PropsWithChildren } from 'lib-shared-types-utils';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useConversationSummaryLanguage,
  useConversationSummaryVisibility,
} from './utils';

export type ConversationContextValue = {
  /** The conversation data. */
  readonly conversation?: Conversation;
  /**
   * The code of the language to which the textual messages should be translated
   * to.
   */
  readonly translationLanguageCode?: ConversationLanguageCode;
  /** The function for settings the translation language code. */
  readonly setTranslationLanguageCode?: Dispatch<
    SetStateAction<ConversationLanguageCode | undefined>
  >;
} & ReturnType<typeof useConversationSummaryLanguage> &
  ReturnType<typeof useConversationSummaryVisibility>;

export const ConversationContext = createContext<ConversationContextValue>({});

type ConversationContextProviderProps = PropsWithChildren & {
  /** The conversation data. */
  conversation: Conversation;
};

export const ConversationContextProvider = ({
  conversation,
  children,
}: ConversationContextProviderProps): JSX.Element => {
  const csid = conversation.id ?? -1;
  const channel = conversation.initial_channel;
  const [translationLanguageCode, setTranslationLanguageCode] =
    useState<ConversationLanguageCode>();

  const visibilityValues = useConversationSummaryVisibility(csid, channel);
  const languageValues = useConversationSummaryLanguage();

  const value = useMemo<ConversationContextValue>(
    () => ({
      ...visibilityValues,
      ...languageValues,
      translationLanguageCode,
      setTranslationLanguageCode,
      conversation,
    }),
    [visibilityValues, languageValues, translationLanguageCode, conversation],
  );

  // Resets the translation language code when conversation id changes.
  useEffect(() => setTranslationLanguageCode(undefined), [csid]);

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};
